import { BALL } from '../core/config.js';

/**
 * The energy ball. Speed is stored separately from the direction so the
 * acceleration ramp and the Slow capsule can adjust pace without touching aim.
 */

export function createBall({ x, y, speed = BALL.baseSpeed, angle = -Math.PI / 3 }) {
  const ball = {
    x,
    y,
    r: BALL.radius,
    speed,
    vx: 0,
    vy: 0,
    /** True while resting on the paddle waiting for launch. */
    stuck: false,
    /** Offset from paddle centre while stuck. */
    stuckOffset: 0,
    stuckTimer: 0,
    trail: [],
  };
  setBallAngle(ball, angle);
  return ball;
}

export function setBallAngle(ball, angle) {
  ball.vx = Math.cos(angle) * ball.speed;
  ball.vy = Math.sin(angle) * ball.speed;
  enforceMinVertical(ball);
}

export function setBallSpeed(ball, speed) {
  const next = Math.max(120, Math.min(BALL.maxSpeed, speed));
  const current = Math.hypot(ball.vx, ball.vy) || 1;
  ball.speed = next;
  ball.vx = (ball.vx / current) * next;
  ball.vy = (ball.vy / current) * next;
}

/**
 * Stop the ball from creeping toward a flat horizontal path, which in the
 * original would leave it pinballing along a brick row forever.
 */
export function enforceMinVertical(ball) {
  const speed = Math.hypot(ball.vx, ball.vy) || ball.speed;
  const minVy = speed * BALL.minVerticalRatio;
  if (Math.abs(ball.vy) < minVy) {
    const sign = ball.vy === 0 ? -1 : Math.sign(ball.vy);
    ball.vy = sign * minVy;
    const remaining = Math.max(0, speed * speed - ball.vy * ball.vy);
    ball.vx = Math.sign(ball.vx || 1) * Math.sqrt(remaining);
  }
  ball.speed = speed;
}

export function pushTrail(ball) {
  ball.trail.push(ball.x, ball.y);
  const max = BALL.trailLength * 2;
  if (ball.trail.length > max) ball.trail.splice(0, ball.trail.length - max);
}

/** Attach the ball to the paddle, remembering where along it landed. */
export function stickBall(ball, paddle) {
  ball.stuck = true;
  ball.stuckTimer = 0;
  ball.stuckOffset = Math.max(
    -paddle.w / 2 + ball.r,
    Math.min(paddle.w / 2 - ball.r, ball.x - paddle.cx),
  );
  ball.y = paddle.y - ball.r - 1;
  ball.trail.length = 0;
}

/** Fire a stuck ball off the paddle, aimed by where it sat. */
export function releaseBall(ball, paddle) {
  if (!ball.stuck) return;
  ball.stuck = false;
  const offset = paddle.w > 0 ? ball.stuckOffset / (paddle.w / 2) : 0;
  const angle = -Math.PI / 2 + offset * BALL.maxDeflect * 0.8;
  setBallAngle(ball, angle);
}

/** Keep a stuck ball glued to the paddle as it moves. */
export function followPaddle(ball, paddle, dt) {
  ball.stuckTimer += dt;
  const limit = paddle.w / 2 - ball.r;
  ball.stuckOffset = Math.max(-limit, Math.min(limit, ball.stuckOffset));
  ball.x = paddle.cx + ball.stuckOffset;
  ball.y = paddle.y - ball.r - 1;
}
